import { ZERO_WEI } from '@kwenta/sdk/constants'
import { FuturesMarketAsset } from '@kwenta/sdk/types'
import { getDisplayAsset, MarketKeyByAsset, getMarketName } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import useClickOutside from 'hooks/useClickOutside'
import { setOpenModal } from 'state/app/reducer'
import { selectMarketAsset, selectOpenMarketDropdownList } from 'state/futures/common/selectors'
import { selectMarketInfo, selectMarkPriceInfos } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectPreviousDayPrices } from 'state/prices/selectors'
import { getSynthDescription } from 'utils/futures'

import { MARKETS_DETAILS_HEIGHT_DESKTOP } from '../styles'

import MarketsDropdownSelector, { MARKET_SELECTOR_HEIGHT_MOBILE } from './MarketsDropdownSelector'

type MarketsDropdownProps = {
	mobile?: boolean
	onClick: () => void
}

const MarketsDropdownHeader: React.FC<MarketsDropdownProps> = ({ mobile, onClick }) => {
	const dispatch = useAppDispatch()
	const markPrices = useAppSelector(selectMarkPriceInfos)
	const pastPrices = useAppSelector(selectPreviousDayPrices)
	const marketAsset = useAppSelector(selectMarketAsset)
	const openMarketDropdownList = useAppSelector(selectOpenMarketDropdownList)

	const marketInfo = useAppSelector(selectMarketInfo)

	const { ref } = useClickOutside(() => {
		if (openMarketDropdownList) {
			dispatch(setOpenModal(null))
		}
	})

	const { t } = useTranslation()

	const getBasePriceRateInfo = useCallback(
		(asset: FuturesMarketAsset) => {
			return markPrices[MarketKeyByAsset[asset]]
		},
		[markPrices]
	)

	const getPastPrice = useCallback(
		(asset: string) =>
			pastPrices.find((price) => price.synth === getDisplayAsset(asset)?.toUpperCase()),
		[pastPrices]
	)

	const selectedBasePriceRate = getBasePriceRateInfo(marketAsset)
	const selectedPastPrice = getPastPrice(marketAsset)

	return (
		<SelectContainer mobile={mobile} ref={ref}>
			<MarketsDropdownSelector
				onClick={onClick}
				expanded={openMarketDropdownList}
				mobile={mobile}
				asset={marketAsset}
				label={getMarketName(marketAsset)}
				description={getSynthDescription(marketAsset, t)}
				isMarketClosed={marketInfo?.isSuspended}
				closureReason={marketInfo?.marketClosureReason}
				priceDetails={{
					oneDayChange:
						selectedBasePriceRate?.price &&
						selectedPastPrice?.rate &&
						selectedBasePriceRate.price.gt(0)
							? wei(selectedBasePriceRate.price)
									.sub(selectedPastPrice.rate)
									.div(selectedBasePriceRate.price)
							: ZERO_WEI,
					priceInfo: selectedBasePriceRate,
				}}
			/>
		</SelectContainer>
	)
}

const SelectContainer = styled.div<{ mobile?: boolean }>`
	height: ${MARKETS_DETAILS_HEIGHT_DESKTOP}px;
	position: relative;

	${(props) =>
		props.mobile &&
		css`
			width: 100%;
			border-bottom: ${props.theme.colors.selectedTheme.border};
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			height: ${MARKET_SELECTOR_HEIGHT_MOBILE + 1}px;
		`}
`

export default MarketsDropdownHeader
